import React, { useState, useEffect } from 'react';
import { Site } from '../../../../types/organisation/sites.types';
import { useCertificationTypes } from '../../hooks/useReferenceData';
import { Plus, Trash2, Award, X, History } from 'lucide-react';

interface PendingCertificationChange {
  type: 'add' | 'remove' | 'modify';
  certId?: number;
  data?: {
    certificationTypeCode: string;
    certificationDate: string;
    expiryDate?: string;
    certificateNumber: string;
  };
}

interface NewCertification {
  certificationTypeCode: string;
  certificationDate: string;
  expiryDate?: string;
  certificateNumber: string;
}

interface SiteCertificationManagerProps {
  site: Site;
  onCertificationChange?: (change: PendingCertificationChange) => void;
  pendingCertificationChanges?: PendingCertificationChange[];
}

const SiteCertificationManager: React.FC<SiteCertificationManagerProps> = ({
  site,
  onCertificationChange,
  pendingCertificationChanges = []
}) => {
  const [currentSite, setCurrentSite] = useState<Site>(site);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newCert, setNewCert] = useState<NewCertification>({
    certificationTypeCode: '', certificationDate: '', expiryDate: '', certificateNumber: ''
  });
  const [editingData, setEditingData] = useState<NewCertification>({
    certificationTypeCode: '', certificationDate: '', expiryDate: '', certificateNumber: ''
  });

  const { certificationTypes } = useCertificationTypes();

  useEffect(() => {
    if (pendingCertificationChanges.length === 0) {
      setCurrentSite(site);
    }
  }, [site.id, site.certifications?.length, pendingCertificationChanges.length]);

  const certifications = currentSite.certifications || [];
  const today = new Date().toISOString().split('T')[0];

  const getStatus = (cert: Record<string, unknown>) => {
    if (cert.isExpired) return 'expired';
    if (cert.isExpiringSoon) return 'expiring';
    return 'active';
  };

  const handleAdd = () => {
    if (!newCert.certificationTypeCode || !newCert.certificationDate || !newCert.certificateNumber) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    onCertificationChange?.({ type: 'add', data: { ...newCert } });

    const selectedType = certificationTypes.find(t => t.code === newCert.certificationTypeCode);
    setCurrentSite(prev => ({
      ...prev,
      certifications: [...(prev.certifications || []), {
        id: Date.now(), // ID temporaire
        site: { id: site.id, siteCode: site.siteCode, siteName: site.siteName, isMainSite: site.isMainSite },
        certificationType: { code: `TEMP_${newCert.certificationTypeCode}`, name: selectedType?.name || '' },
        certificationDate: newCert.certificationDate,
        expiryDate: newCert.expiryDate,
        certificateNumber: newCert.certificateNumber,
        isActive: true,
        isExpired: false,
        isExpiringSoon: false
      }]
    }));

    setNewCert({ certificationTypeCode: '', certificationDate: '', expiryDate: '', certificateNumber: '' });
    setShowAddForm(false);
  };

  const handleStartEdit = (cert: Record<string, unknown>) => {
    setEditingId(cert.id);
    setEditingData({
      certificationTypeCode: cert.certificationType.code.replace('TEMP_', ''),
      certificationDate: cert.certificationDate || '',
      expiryDate: cert.expiryDate || '',
      certificateNumber: cert.certificateNumber || ''
    });
  };

  const handleSaveEdit = () => {
    if (!editingData.certificationTypeCode || !editingData.certificationDate || !editingData.certificateNumber) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (editingId === null) return;

    const isTemp = String(editingId).startsWith('TEMP_') || editingId > 1e12;
    if (!isTemp) {
      onCertificationChange?.({ type: 'modify', certId: editingId, data: { ...editingData } });
    }

    setCurrentSite(prev => ({
      ...prev,
      certifications: (prev.certifications || []).map(c => {
        if (c.id !== editingId) return c;
        const selectedType = certificationTypes.find(t => t.code === editingData.certificationTypeCode);
        return {
          ...c,
          certificationType: { code: editingData.certificationTypeCode, name: selectedType?.name || '' },
          certificationDate: editingData.certificationDate,
          expiryDate: editingData.expiryDate,
          certificateNumber: editingData.certificateNumber
        };
      })
    }));

    setEditingId(null);
  };

  const handleDelete = (cert: Record<string, unknown>) => {
    if (!window.confirm('Supprimer cette certification ?')) return;

    const isTemp = cert.certificationType.code.startsWith('TEMP_');
    if (!isTemp) {
      onCertificationChange?.({ type: 'remove', certId: cert.id });
    }

    setCurrentSite(prev => ({
      ...prev,
      certifications: (prev.certifications || []).filter(c => c.id !== cert.id)
    }));
  };

  return (
    <div className="space-y-3">
      {/* Liste */}
      {certifications.length === 0 && (
        <p className="text-center py-6 text-gray-500 text-sm">Aucune certification</p>
      )}

      {certifications.map(cert => {
        const status = getStatus(cert);
        const isTemp = cert.certificationType.code.startsWith('TEMP_');
        const isEditingThis = editingId === cert.id;

        return (
          <div
            key={cert.id}
            onDoubleClick={() => !isEditingThis && handleStartEdit(cert)}
            className={`p-3 rounded-lg border cursor-pointer ${
              isTemp ? 'bg-blue-50 border-blue-200' :
              status === 'expired' ? 'bg-gray-50 border-gray-200 opacity-75' :
              status === 'expiring' ? 'bg-yellow-50 border-yellow-200' :
              'bg-white border-gray-200 hover:bg-gray-50'
            }`}
          >
            {isEditingThis ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
                    <select
                      value={editingData.certificationTypeCode}
                      onChange={e => setEditingData(p => ({ ...p, certificationTypeCode: e.target.value }))}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sélectionner</option>
                      {certificationTypes.map(t => (
                        <option key={t.code} value={t.code}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">N° certificat *</label>
                    <input type="text" value={editingData.certificateNumber}
                      onChange={e => setEditingData(p => ({ ...p, certificateNumber: e.target.value }))}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date certification *</label>
                    <input type="date" value={editingData.certificationDate}
                      onChange={e => setEditingData(p => ({ ...p, certificationDate: e.target.value }))}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date expiration</label>
                    <input type="date" value={editingData.expiryDate}
                      onChange={e => setEditingData(p => ({ ...p, expiryDate: e.target.value }))}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEditingId(null)}
                    className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                    Annuler
                  </button>
                  <button onClick={handleSaveEdit}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                    Sauvegarder
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2 flex-1">
                  {status === 'expired'
                    ? <History className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                    : <Award className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  }
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900">
                        {cert.certificationType.name}
                      </span>
                      {isTemp && <span className="text-xs text-blue-600 font-medium">(Nouvelle)</span>}
                      {status === 'active' && !isTemp &&
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>}
                      {status === 'expiring' &&
                        <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Expire bientôt</span>}
                      {status === 'expired' &&
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Expirée</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {cert.certificateNumber && <span className="font-mono">{cert.certificateNumber}</span>}
                      {cert.certificationDate && <span> · Du {cert.certificationDate}</span>}
                      {cert.expiryDate && <span> au {cert.expiryDate}</span>}
                    </div>
                  </div>
                </div>
                <button onClick={() => handleDelete(cert)}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded shrink-0 ml-2">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Bouton ajouter */}
      {!showAddForm && (
        <button onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
          <Plus className="h-4 w-4" />
          Ajouter une certification
        </button>
      )}

      {/* Formulaire ajout */}
      {showAddForm && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-medium text-blue-900 flex items-center gap-2 text-sm">
              <Award className="h-4 w-4" />
              Nouvelle certification
            </h4>
            <button onClick={() => setShowAddForm(false)} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type *</label>
              <select value={newCert.certificationTypeCode}
                onChange={e => setNewCert(p => ({ ...p, certificationTypeCode: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                <option value="">Sélectionner un type</option>
                {certificationTypes.map(t => (
                  <option key={t.code} value={t.code}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">N° certificat *</label>
              <input type="text" value={newCert.certificateNumber}
                onChange={e => setNewCert(p => ({ ...p, certificateNumber: e.target.value }))}
                placeholder="Ex: ISO9001-2025-001"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date certification *</label>
              <input type="date" value={newCert.certificationDate}
                onChange={e => setNewCert(p => ({ ...p, certificationDate: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date expiration</label>
              <input type="date" value={newCert.expiryDate}
                onChange={e => setNewCert(p => ({ ...p, expiryDate: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setShowAddForm(false)}
              className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
              Annuler
            </button>
            <button onClick={handleAdd}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
              Ajouter
            </button>
          </div>
        </div>
      )}

      {pendingCertificationChanges.length > 0 && (
        <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
          {pendingCertificationChanges.length} modification(s) en attente — sauvegardez pour confirmer
        </p>
      )}
    </div>
  );
};

export default SiteCertificationManager;
